import { useRef } from 'react';
import {
  ListItem,
  ListItemButton,
  ListItemText,
  Typography,
  Checkbox,
  Box,
  TextField
} from '@mui/material';
import { formatCurrency } from '@utils/numberFormatters';
import type { RepairPartViewModel } from '@api/enterprise/openapi';

type Props = {
  part: RepairPartViewModel;
  isSelected: boolean;
  quantity: number;
  onToggle: () => void;
  onQuantityChange: (quantity: number) => void;
};

export const PartListItem = ({
  part,
  isSelected,
  quantity,
  onToggle,
  onQuantityChange
}: Props) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const blurTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  return (
    <ListItem
      disablePadding
      secondaryAction={
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {isSelected && (
            <TextField
              type="text"
              value={quantity}
              inputRef={inputRef}
              onChange={e => {
                if (blurTimeoutRef.current) {
                  clearTimeout(blurTimeoutRef.current);
                }

                const val = e.target.value;
                if (val === '') {
                  onQuantityChange(1);
                  return;
                }
                if (/^\d+$/.test(val)) {
                  const numVal = parseInt(val);
                  if (numVal > 0 && numVal <= 9999) {
                    onQuantityChange(numVal);

                    blurTimeoutRef.current = setTimeout(() => {
                      inputRef.current?.blur();
                    }, 500);
                  }
                }
              }}
              onFocus={e => e.target.select()}
              onClick={e => {
                e.stopPropagation();
                (e.target as HTMLInputElement).select();
              }}
              size="small"
              sx={{ width: 70 }}
              inputProps={{
                style: { textAlign: 'center' },
                inputMode: 'numeric',
                pattern: '[0-9]*'
              }}
            />
          )}
          <Checkbox edge="end" checked={isSelected} onChange={onToggle} />
        </Box>
      }
    >
      <ListItemButton onClick={onToggle}>
        <ListItemText
          primary={
            <Typography variant="body1" fontWeight={500}>
              {part.partNumber}
            </Typography>
          }
          secondary={
            <Box component="span">
              {part.description && (
                <Typography
                  component="span"
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    display: 'block',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap'
                  }}
                >
                  {part.description}
                </Typography>
              )}
              {part.cost && (
                <Typography
                  component="span"
                  variant="body2"
                  color="text.secondary"
                  sx={{ display: 'block' }}
                >
                  Cost: {formatCurrency(part.cost)}
                </Typography>
              )}
            </Box>
          }
        />
      </ListItemButton>
    </ListItem>
  );
};
